import { useEffect, useState } from "react";
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, getRooms } from "../../firebase/services";
import { EmptyState, Spinner, Modal, Btn, Input } from "../../components/UI";

const CATEGORIES = {
  info:        { label: "Information",  icon: "ℹ️",  color: "#1a3a5c", bg: "#e3f1fb" },
  urgent:      { label: "Urgent",       icon: "🚨",  color: "#a32d2d", bg: "#fdecea" },
  maintenance: { label: "Maintenance",  icon: "🔧",  color: "#8a4c00", bg: "#fff3e0" },
  event:       { label: "Événement",    icon: "🎉",  color: "#2d6a0a", bg: "#e8f5e0" },
};

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `il y a ${Math.round(s/60)} min`;
  if (s < 86400) return `il y a ${Math.round(s/3600)}h`;
  return `il y a ${Math.round(s/86400)}j`;
}

const EMPTY = { title: "", body: "", category: "info", targetRoomId: null };

export default function OwnerAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [rooms, setRooms]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [modal, setModal]                 = useState(false);
  const [editTarget, setEditTarget]       = useState(null);
  const [form, setForm]                   = useState(EMPTY);
  const [saving, setSaving]               = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const unsubs = [
      getAnnouncements(data => { setAnnouncements(data); setLoading(false); }),
      getRooms(setRooms),
    ];
    return () => unsubs.forEach(u => u && u());
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditTarget(null);
    setModal(true);
  };

  const openEdit = (a) => {
    setForm({ title: a.title, body: a.body, category: a.category, targetRoomId: a.targetRoomId || null });
    setEditTarget(a);
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    if (editTarget) {
      await updateAnnouncement(editTarget.id, form);
    } else {
      await createAnnouncement(form);
    }
    setSaving(false);
    setModal(false);
  };

  const handleDelete = async (id) => {
    await deleteAnnouncement(id);
    setDeleteConfirm(null);
  };

  if (loading) return <div style={S.page}><Spinner /></div>;

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div>
          <h1 style={S.title}>Annonces</h1>
          <p style={S.sub}>{announcements.length} annonce{announcements.length > 1 ? "s" : ""}</p>
        </div>
        <button style={S.addBtn} onClick={openNew}>+ Nouvelle</button>
      </div>

      <div style={S.scroll}>
        {announcements.length === 0 ? (
          <EmptyState icon="📢" title="Aucune annonce" subtitle="Créez une annonce pour informer vos étudiants" />
        ) : announcements.map(a => {
          const cat = CATEGORIES[a.category] || CATEGORIES.info;
          const targetRoom = a.targetRoomId ? rooms.find(r => r.id === a.targetRoomId) : null;
          return (
            <div key={a.id} style={{ ...S.card, borderLeft: `4px solid ${cat.color}` }}>
              <div style={S.cardTop}>
                <div style={{ ...S.catBadge, background: cat.bg, color: cat.color }}>
                  {cat.icon} {cat.label}
                </div>
                {targetRoom && (
                  <div style={S.targetBadge}>📍 Ch. {targetRoom.number}</div>
                )}
                {!a.targetRoomId && (
                  <div style={S.allBadge}>👥 Toutes les chambres</div>
                )}
                <div style={{ flex: 1 }} />
                <span style={S.timeLabel}>{timeAgo(a.createdAt)}</span>
              </div>
              <p style={S.cardTitle}>{a.title}</p>
              <p style={S.cardBody}>{a.body}</p>
              <div style={S.cardActions}>
                <button style={S.editBtn} onClick={() => openEdit(a)}>✏️ Modifier</button>
                <button style={S.deleteBtn} onClick={() => setDeleteConfirm(a.id)}>🗑️ Supprimer</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal création/édition */}
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? "Modifier l'annonce" : "Nouvelle annonce"}
      >
        <div>
          {/* Catégorie */}
          <p style={S.fieldLabel}>Catégorie</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            {Object.entries(CATEGORIES).map(([key, val]) => (
              <button
                key={key}
                style={{
                  padding: "7px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  border: form.category === key ? `1.5px solid ${val.color}` : "1.5px solid #d0dce8",
                  background: form.category === key ? val.bg : "white",
                  color: form.category === key ? val.color : "#5a7a9a",
                  cursor: "pointer", fontFamily: "inherit"
                }}
                onClick={() => setForm(f => ({ ...f, category: key }))}
              >
                {val.icon} {val.label}
              </button>
            ))}
          </div>

          <Input
            label="Titre *"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Ex: Coupure d'eau prévue, Réunion locataires..."
          />

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: "#4a6a8a", display: "block", marginBottom: 4, fontWeight: 500 }}>
              Message *
            </label>
            <textarea
              style={{ width: "100%", border: "1.5px solid #d0dce8", borderRadius: 10, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", background: "#f7fafd", color: "#1a2a3a", resize: "none", outline: "none", boxSizing: "border-box", minHeight: 90 }}
              value={form.body}
              onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              placeholder="Détails de l'annonce..."
            />
          </div>

          {/* Destinataires */}
          <p style={S.fieldLabel}>Destinataires</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            <label style={S.radioRow}>
              <input type="radio" checked={!form.targetRoomId}
                onChange={() => setForm(f => ({ ...f, targetRoomId: null }))} />
              <span style={{ fontSize: 13 }}>👥 Toutes les chambres</span>
            </label>
            <label style={S.radioRow}>
              <input type="radio" checked={!!form.targetRoomId}
                onChange={() => setForm(f => ({ ...f, targetRoomId: rooms[0]?.id || null }))} />
              <span style={{ fontSize: 13 }}>📍 Une chambre spécifique</span>
            </label>
            {form.targetRoomId && (
              <select
                style={{ border: "1.5px solid #d0dce8", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", background: "#f7fafd", color: "#1a2a3a", outline: "none" }}
                value={form.targetRoomId}
                onChange={e => setForm(f => ({ ...f, targetRoomId: e.target.value }))}
              >
                {rooms.map(r => (
                  <option key={r.id} value={r.id}>
                    Chambre {r.number}{r.tenantName ? ` — ${r.tenantName}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <Btn variant="primary" onClick={handleSave} disabled={saving || !form.title.trim() || !form.body.trim()}>
            {saving ? "Publication..." : editTarget ? "✓ Enregistrer" : "📢 Publier l'annonce"}
          </Btn>
        </div>
      </Modal>

      {/* Confirmation suppression */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Supprimer l'annonce">
        <div>
          <p style={{ fontSize: 14, color: "#4a6a8a", marginBottom: 16 }}>
            Cette annonce sera supprimée définitivement.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="danger" onClick={() => handleDelete(deleteConfirm)}>Oui, supprimer</Btn>
            <Btn variant="ghost" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const S = {
  page:    { display: "flex", flexDirection: "column", height: "100%", background: "#f5f8fc", fontFamily: "'Barlow',sans-serif" },
  topBar:  { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px 6px" },
  title:   { fontSize: 22, fontWeight: 800, color: "#1a3a5c", fontFamily: "'Barlow Condensed',sans-serif", margin: 0 },
  sub:     { fontSize: 12, color: "#7a90a8", margin: "2px 0 0" },
  addBtn:  { background: "#1a3a5c", color: "white", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  scroll:  { flex: 1, overflowY: "auto", padding: "0 16px 80px" },
  card:    { background: "white", borderRadius: "0 14px 14px 0", padding: "14px", marginBottom: 10, borderTop: "0.5px solid #d0dce8", borderRight: "0.5px solid #d0dce8", borderBottom: "0.5px solid #d0dce8" },
  cardTop: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" },
  catBadge:{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "3px 10px" },
  targetBadge: { fontSize: 11, background: "#e3f1fb", color: "#1a3a5c", borderRadius: 20, padding: "3px 10px", fontWeight: 600 },
  allBadge:    { fontSize: 11, background: "#f0f4f8", color: "#5a7a9a", borderRadius: 20, padding: "3px 10px" },
  timeLabel:   { fontSize: 11, color: "#aab8c8" },
  cardTitle:   { fontSize: 15, fontWeight: 700, color: "#1a2a3a", margin: "0 0 6px" },
  cardBody:    { fontSize: 13, color: "#4a6a8a", lineHeight: 1.6, margin: "0 0 10px" },
  cardActions: { display: "flex", gap: 8 },
  editBtn:     { padding: "6px 12px", borderRadius: 8, background: "#f0f4f8", color: "#1a3a5c", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  deleteBtn:   { padding: "6px 12px", borderRadius: 8, background: "#fdecea", color: "#a32d2d", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  fieldLabel:  { fontSize: 12, color: "#4a6a8a", fontWeight: 500, marginBottom: 8 },
  radioRow:    { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 },
};
